import React, { useState, useEffect, useRef } from "react";
import {
  IconBus,
  IconClock,
  IconCalendar,
  IconError,
  IconChevronUp,
  IconChevronDown,
  IconStop,
  IconViewOnMap,
  IconBack,
} from "../../components/ui/icons";
import { busApi } from "../../services/api/busApi";
import { useVehicleTracking } from "../../hooks/useVehicleTracking";

const toRad = (deg) => (deg * Math.PI) / 180;

const distanceMetersBetween = (a, b) => {
  // Haversine (suficiente e estável para distâncias pequenas)
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  const h = s1 * s1 + Math.cos(lat1) * Math.cos(lat2) * s2 * s2;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return R * c;
};

const resolveNextStopName = (vehicle, stops) => {
  if (!vehicle) return null;
  if (!Array.isArray(stops) || stops.length === 0) return null;
  if (typeof vehicle.lat !== "number" || typeof vehicle.lng !== "number") {
    return null;
  }

  const stopsWithCoords = stops.filter(
    (s) => typeof s?.lat === "number" && typeof s?.lng === "number"
  );
  if (stopsWithCoords.length === 0) return null;

  let nearestStop = null;
  let nearestDist = Number.POSITIVE_INFINITY;
  for (const stop of stopsWithCoords) {
    const d = distanceMetersBetween(
      { lat: vehicle.lat, lng: vehicle.lng },
      { lat: stop.lat, lng: stop.lng }
    );
    if (d < nearestDist) {
      nearestDist = d;
      nearestStop = stop;
    }
  }

  if (!nearestStop) return null;

  // Se tivermos sequência, tenta pegar a próxima
  const nearestSeq =
    typeof nearestStop.sequence === "number" ? nearestStop.sequence : null;
  if (nearestSeq !== null) {
    const stopsBySeq = stopsWithCoords
      .filter((s) => typeof s.sequence === "number")
      .slice()
      .sort((a, b) => a.sequence - b.sequence);

    const next =
      stopsBySeq.find((s) => s.sequence === nearestSeq + 1) ||
      stopsBySeq.find((s) => s.sequence > nearestSeq) ||
      null;

    if (next?.stopName) return next.stopName;
  }

  return nearestStop.stopName || null;
};

// Operacional Cittati estacionado (T03/T04): a grade da linha usa só horários GTFS.

function PartidasLinha({
  routeId,
  routeShortName,
  onBack,
  onVehiclesUpdate,
  onVehicleClick,
}) {
  const [partidas, setPartidas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(0);
  // GTFS: substituiu selectedTrip — agora agrupa por directionId (0=ida, 1=volta)
  const [selectedDirection, setSelectedDirection] = useState(null);
  const [vehiclesExpanded, setVehiclesExpanded] = useState(false);

  // O routeId recebido via prop já é o GTFS route_id correto (vem de /api/planner/routes).
  // Usar o routeId prop diretamente evita que o shortName (ex: "03AI") sobrescreva o ID correto.
  const gtfsRouteId = React.useMemo(() => routeId ?? null, [routeId]);

  const tripStopsCacheRef = useRef(new Map());
  const [nextStopByVehicleId, setNextStopByVehicleId] = useState({});
  const [nextStopLoadingByVehicleId, setNextStopLoadingByVehicleId] = useState(
    {}
  );

  const allVehicleFilters = React.useMemo(() => {
    if (!gtfsRouteId && !routeShortName) return {};

    const filters = {};
    if (routeShortName) {
      // Contrato operacional: line = route_short_name (nunca routeId)
      filters.line = routeShortName;
    } else if (gtfsRouteId) {
      filters.routeId = gtfsRouteId;
    }

    return filters;
  }, [gtfsRouteId, routeShortName]);

  const {
    vehicles: vehiclesFromApi,
    loading: vehiclesLoading,
    source: vehiclesSource,
    degraded: vehiclesDegraded,
    warning: vehiclesWarning,
    lastSuccessfulAt: vehiclesLastSuccessfulAt,
  } = useVehicleTracking(allVehicleFilters, 15000, !!(gtfsRouteId || routeShortName));

  const getVehicleDirectionText = React.useCallback((vehicle) => {
    let label = vehicle?.directionLabel || null;
    if (typeof label === 'string' && (label.toUpperCase() === 'IDA' || label.toUpperCase() === 'VOLTA')) {
      label = 'Sentido';
    }

    const terminus = vehicle?.directionTerminus || null;

    if (label && terminus) return `${label} → ${terminus}`;
    if (label) return String(label);

    const numeric = Number(vehicle?.directionId);
    if (numeric === 0 || numeric === 1) return "Sentido";

    return "Sentido indefinido";
  }, []);

  const dayCategories = React.useMemo(() => {
    if (!partidas?.dayCategories) return [];
    const cats = Array.isArray(partidas.dayCategories) ? partidas.dayCategories : [];
    return cats;
  }, [partidas]);

  // Manter índices válidos quando o filtro mudar
  useEffect(() => {
    setSelectedCategory(0);
    setSelectedDirection(null);
  }, [routeId, partidas]);

  const vehicles = React.useMemo(() => {
    const list = Array.isArray(vehiclesFromApi) ? vehiclesFromApi : [];
    if (!gtfsRouteId) return list;
    return list.filter((v) => String(v?.routeId || "") === String(gtfsRouteId));
  }, [vehiclesFromApi, gtfsRouteId]);

  // Notificar componente pai sobre veículos
  useEffect(() => {
    if (onVehiclesUpdate) {
      onVehiclesUpdate(vehicles, {
        source: vehiclesSource,
        degraded: vehiclesDegraded,
        warning: vehiclesWarning,
        lastSuccessfulAt: vehiclesLastSuccessfulAt,
      });
    }
  }, [
    vehicles,
    onVehiclesUpdate,
    vehiclesSource,
    vehiclesDegraded,
    vehiclesWarning,
    vehiclesLastSuccessfulAt,
  ]);

  useEffect(() => {
    if (!vehiclesExpanded) return;
    if (!Array.isArray(vehicles) || vehicles.length === 0) return;

    let cancelled = false;

    const isNonEmptyString = (v) =>
      typeof v === "string" && v.trim().length > 0;

    const loadNextStops = async () => {
      const nextStopUpdates = {};
      const loadingUpdates = {};

      for (const vehicle of vehicles) {
        const vehicleId = vehicle?.vehicleId;
        if (!vehicleId) continue;

        const tripId =
          (isNonEmptyString(vehicle?.tripId) && vehicle.tripId.trim()) ||
          (isNonEmptyString(vehicle?.tripIdRaw) && vehicle.tripIdRaw.trim()) ||
          null;

        if (!isNonEmptyString(tripId)) {
          nextStopUpdates[vehicleId] = null;
          loadingUpdates[vehicleId] = false;
          continue;
        }

        const cachedStops = tripStopsCacheRef.current.get(tripId);
        if (cachedStops) {
          nextStopUpdates[vehicleId] = resolveNextStopName(
            vehicle,
            cachedStops
          );
          loadingUpdates[vehicleId] = false;
          continue;
        }

        loadingUpdates[vehicleId] = true;
        if (!cancelled) {
          setNextStopLoadingByVehicleId((prev) => ({
            ...prev,
            [vehicleId]: true,
          }));
        }

        try {
          const res = await busApi.getTripShape(tripId, { includeStops: true });
          const stops = Array.isArray(res?.data?.stops) ? res.data.stops : [];
          tripStopsCacheRef.current.set(tripId, stops);
          nextStopUpdates[vehicleId] = resolveNextStopName(vehicle, stops);
        } catch (err) {
          console.error("Erro ao buscar paradas do trip para próxima parada:", {
            err,
            tripId,
            vehicleId,
          });
          nextStopUpdates[vehicleId] = null;
        } finally {
          loadingUpdates[vehicleId] = false;
        }
      }

      if (cancelled) return;

      setNextStopByVehicleId((prev) => ({
        ...prev,
        ...nextStopUpdates,
      }));

      setNextStopLoadingByVehicleId((prev) => ({
        ...prev,
        ...loadingUpdates,
      }));
    };

    loadNextStops();

    return () => {
      cancelled = true;
    };
  }, [vehiclesExpanded, vehicles]);

  useEffect(() => {
    if (!routeId && !routeShortName) return;

    const fetchData = async () => {
      setLoading(true);
      setError("");
      setPartidas(null);

      try {
        // Fonte única: horários GTFS estático da linha (operacional estacionado).
        if (routeId) {
          const gtfsResult = await busApi.getDeparturesByRoute(routeId);
          setPartidas(gtfsResult?.data ?? null);
        }
        setSelectedCategory(0);
        setSelectedDirection(null);
      } catch (err) {
        if (routeId) {
          setError("Não foi possível carregar os horários GTFS da linha.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [routeId, routeShortName]);

  if (!routeId && !routeShortName) {
    return null;
  }

  const safeCategoryIndex = Math.min(selectedCategory, Math.max(0, dayCategories.length - 1));
  const currentCategory = dayCategories?.[safeCategoryIndex];

  // Novo formato GTFS: cada trip em trips[] já é um horário (tem departureTime direto).
  // Agrupa sentidos únicos por directionId para montar abas de sentido.
  const directions = (() => {
    if (!currentCategory?.trips) return [];
    const seen = new Set();
    return currentCategory.trips.reduce((acc, trip) => {
      const dir = trip.directionId ?? 0;
      if (!seen.has(dir)) {
        seen.add(dir);
        acc.push({ directionId: dir, label: 'Sentido', headsign: trip.headsign });
      }
      return acc;
    }, []);
  })();

  const activeDirectionId = selectedDirection ?? directions[0]?.directionId ?? 0;
  const activeDirection = directions.find(d => d.directionId === activeDirectionId) ?? directions[0];

  const filteredTrips = (() => {
    if (!currentCategory?.trips) return [];
    if (directions.length <= 1) return currentCategory.trips;
    return currentCategory.trips.filter(t => (t.directionId ?? 0) === activeDirectionId);
  })();

  return (
    <div className="sheet-partidas-linha-container">
      <button onClick={onBack} className="back-button">
        <IconBack size={18} aria-hidden="true" /> Voltar
      </button>

      {loading && (
        <div className="px-5 py-10 text-center">
          <div className="loading-spinner" aria-hidden="true" />
          <p className="mt-4 text-muted">Carregando horários...</p>
        </div>
      )}

      {error && (
        <div className="rounded-[8px] bg-danger/10 px-5 py-5 text-center text-danger">
          <p className="flex items-center justify-center gap-2">
            <IconError size={16} aria-hidden="true" /> {error}
          </p>
        </div>
      )}

      {partidas && (
        <div>
          {/* HEADER DA LINHA */}
          <div className="linha-header">
            <div
              className="linha-badge"
              style={{ backgroundColor: partidas?.color || "#2563eb" }}
            >
              {partidas?.shortName || routeShortName || "-"}
            </div>
            <div className="linha-info">
              <h2>{partidas?.longName || "Linha selecionada"}</h2>
              <p>
                {dayCategories.length}{" "}
                {dayCategories.length === 1
                  ? "categoria"
                  : "categorias"}{" "}
                de dia
              </p>
            </div>
          </div>

          {/* Informação de ônibus em tempo real */}
          <div
            className={`mb-4 rounded-[8px] border p-3 ${
              vehicles.length > 0
                ? "border-primary/20 bg-primary/5"
                : "border-danger/20 bg-danger/5"
            }`}
          >
            <div
              className={`flex items-center justify-between ${
                vehicles.length > 0 ? "cursor-pointer" : "cursor-default"
              }`}
              onClick={() =>
                vehicles.length > 0 && setVehiclesExpanded(!vehiclesExpanded)
              }
            >
              <div className="flex items-center gap-2">
                <IconBus
                  size={22}
                  aria-hidden="true"
                  className={vehicles.length > 0 ? "text-primary" : "text-danger"}
                />
                <div>
                  {vehicles.length > 0 ? (
                    <>
                      <strong className="flex items-center gap-2 text-primary">
                        <span
                          className="relative flex h-2 w-2"
                          aria-hidden="true"
                          title="Ao vivo"
                        >
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
                        </span>
                        {vehicles.length} ônibus em circulação
                      </strong>
                      <div className="text-xs text-muted">
                        {vehiclesLoading
                          ? "Atualizando..."
                          : "Clique para ver detalhes"}
                      </div>
                    </>
                  ) : (
                    <>
                      <strong className="text-danger">
                        Nenhum ônibus em circulação
                      </strong>
                      <div className="text-xs text-muted">
                        {vehiclesLoading
                          ? "Buscando..."
                          : "Sem dados em tempo real no momento"}
                      </div>
                    </>
                  )}
                </div>
              </div>
              {vehicles.length > 0 && (
                <span className="inline-flex text-primary">
                  {vehiclesExpanded ? (
                    <IconChevronUp size={18} aria-hidden="true" />
                  ) : (
                    <IconChevronDown size={18} aria-hidden="true" />
                  )}
                </span>
              )}
            </div>

            {/* Lista de veículos expansível */}
            {vehiclesExpanded && vehicles.length > 0 && (
              <div className="mt-3 border-t border-primary/20 pt-3">
                {vehicles.map((vehicle, index) => {
                  const vehicleDisplayId =
                    vehicle.vehiclePrefix ??
                    vehicle.vehicleId ??
                    vehicle.plate ??
                    null;

                  return (
                    <div
                      key={vehicle.vehicleId || index}
                      className="mb-2 flex items-center justify-between rounded-[6px] border border-primary/15 bg-surface p-2 last:mb-0"
                    >
                      <div className="flex-1">
                        <div className="mb-1 flex items-center gap-1.5 font-semibold text-ink">
                          <IconBus size={14} aria-hidden="true" className="shrink-0" />{" "}
                          Veículo #{vehicleDisplayId || "-"}
                        </div>
                        <div className="text-xs text-slate-700">
                          {getVehicleDirectionText(vehicle)}
                        </div>
                        <div className="flex items-start gap-1.5 text-xs text-muted">
                          <IconStop size={12} aria-hidden="true" className="mt-0.5 shrink-0" />
                          <span>
                            {nextStopLoadingByVehicleId[vehicle.vehicleId]
                              ? "Buscando próxima parada..."
                              : nextStopByVehicleId[vehicle.vehicleId]
                              ? `Próxima parada: ${
                                  nextStopByVehicleId[vehicle.vehicleId]
                                }`
                              : "Próxima parada indisponível"}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onVehicleClick) {
                            onVehicleClick(vehicle);
                          }
                        }}
                        style={{
                          padding: "8px 14px",
                          minHeight: "44px",
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          whiteSpace: "nowrap",
                          width: "auto",
                          flexShrink: 0,
                        }}
                      >
                        <IconViewOnMap size={14} aria-hidden="true" /> Ver no mapa
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ABAS DE CATEGORIA DE DIA */}
          <div className="category-tabs">
            {dayCategories.map((category, index) => (
              <button
                key={category.category}
                className={`category-tab ${
                  safeCategoryIndex === index ? "active" : ""
                }`}
                onClick={() => {
                  setSelectedCategory(index);
                  setSelectedDirection(null);
                }}
              >
                {category.categoryName}
              </button>
            ))}
          </div>

          {/* ABAS DE SENTIDO */}
          {directions.length > 1 && (
            <div className="trip-tabs">
              {directions.map((dir) => (
                <button
                  key={dir.directionId}
                  className={`trip-tab ${activeDirectionId === dir.directionId ? "active" : ""}`}
                  onClick={() => setSelectedDirection(dir.directionId)}
                >
                  <IconBus size={14} aria-hidden="true" /> {dir.label} — {dir.headsign}
                </button>
              ))}
            </div>
          )}

          {/* GRID DE HORÁRIOS */}
          {currentCategory && filteredTrips.length > 0 && (
            <div className="horarios-section">
              {directions.length <= 1 && activeDirection && (
                <h3 className="sentido-title">
                  <IconBus size={16} aria-hidden="true" /> Sentido: {activeDirection.headsign}
                </h3>
              )}

              <div className="horarios-info">
                <span>
                  <IconCalendar size={14} aria-hidden="true" /> {currentCategory.categoryName}
                </span>
                <span>
                  <IconClock size={14} aria-hidden="true" /> {filteredTrips.length} horários
                </span>
              </div>

              <div className="horarios-grid">
                {filteredTrips.map((trip, index) => {
                  const time = trip.departureTime.substring(0, 5);
                  const now = new Date();
                  const currentTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
                  const isNext =
                    time > currentTime &&
                    index === filteredTrips.findIndex(
                      (t) => t.departureTime.substring(0, 5) > currentTime
                    );

                  return (
                    <div
                      key={trip.tripId}
                      className={`horario-card ${isNext ? "next" : ""}`}
                    >
                      {time}
                      {isNext && <span className="next-badge">Próximo</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PartidasLinha;
